import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Button, GenericBadge, Icon, Typography } from '@/components/common';
import { TooltipComponent } from '@/components/common/tooltip';
import { useFocusTrap, useNotification } from '@/hooks';
import Icons from '@/const/icons';
import { DetectedPdfField, FieldConfig } from '@/interfaces';
import { DocumentToolbar } from './DocumentToolbar';
import {
	DesktopMobilePreviewModal,
	DesktopMobilePreviewModalHandle,
} from './DesktopMobilePreviewModal';
import { FieldsPanel } from './FieldsPanel';
import { NoFieldsDetectedModal } from './NoFieldsDetectedModal';
import { useFieldConfigs } from './hooks/useFieldConfigs';
import { isConfigurablePdfField } from './lib/pdfFieldDetection';
import { PdfFieldCanvas, PdfFieldCanvasHandle } from './PdfFieldCanvas';
import { useDragToPan } from '@/hooks/useDragToPan';

const ROW_Y_TOLERANCE = 8;

const sortFieldsByReadingOrder = (
	fields: DetectedPdfField[]
): DetectedPdfField[] => {
	const byPageThenY = [...fields].sort((a, b) => {
		if (a.pageIndex !== b.pageIndex) return a.pageIndex - b.pageIndex;
		return b.rect.y - a.rect.y;
	});

	const rows: DetectedPdfField[][] = [];
	byPageThenY.forEach((field) => {
		const currentRow = rows[rows.length - 1];
		const rowAnchor = currentRow?.[0];
		const sameRow =
			rowAnchor &&
			rowAnchor.pageIndex === field.pageIndex &&
			Math.abs(rowAnchor.rect.y - field.rect.y) <= ROW_Y_TOLERANCE;

		if (sameRow) {
			currentRow.push(field);
		} else {
			rows.push([field]);
		}
	});

	return rows.flatMap((row) => [...row].sort((a, b) => a.rect.x - b.rect.x));
};

export interface ConfigureFieldsStepProps {
	pdfUrl: string;
	file: File;
	onBack?: () => void;
	onClose?: () => void;
	onContinue?: (fieldConfigs: Record<string, FieldConfig>) => void;
	initialFieldConfigs?: Record<string, FieldConfig>;
	hasFieldsHint?: boolean;
	onOverlayActiveChange?: (isOverlayActive: boolean) => void;
}

export const ConfigureFieldsStep: React.FC<ConfigureFieldsStepProps> = ({
	pdfUrl,
	file,
	initialFieldConfigs,
	hasFieldsHint,
	onBack,
	onClose,
	onOverlayActiveChange,
	onContinue,
}) => {
	const { toastError } = useNotification();
	const [detectedFields, setDetectedFields] = React.useState<
		DetectedPdfField[]
	>([]);
	const [selectedFieldId, setSelectedFieldId] = React.useState<string | null>(
		null
	);

	const [showConfirmPopover, setShowConfirmPopover] = React.useState(false);
	const confirmPopoverRef = useFocusTrap<HTMLDivElement>(
		showConfirmPopover,
		() => setShowConfirmPopover(false)
	);
	const [hasDetectedFields, setHasDetectedFields] = React.useState(false);
	const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);

	const [zoomScale, setZoomScale] = React.useState(1.15);
	const [numPages, setNumPages] = React.useState(0);
	const [currentPageIndex, setCurrentPageIndex] = React.useState(0);
	const pdfCanvasRef = React.useRef<PdfFieldCanvasHandle>(null);
	const previewModalRef = React.useRef<DesktopMobilePreviewModalHandle>(null);

	const handlePageChange = React.useCallback((pageIndex: number) => {
		pdfCanvasRef.current?.scrollToPage(pageIndex);
	}, []);

	const {
		containerRef: pdfViewportRef,
		canPan: canPanViewport,
		isPanning: isPanningViewport,
	} = useDragToPan();
	const viewportCursorClassName = canPanViewport
		? isPanningViewport
			? 'cursor-grabbing'
			: 'cursor-grab'
		: '';

	const handleFieldsDetected = React.useCallback(
		(fields: DetectedPdfField[]) => {
			setDetectedFields(fields);
			setHasDetectedFields(true);
		},
		[]
	);

	const handlePdfLoadError = React.useCallback(() => {
		toastError(
			'There was an error with this PDF file. Please try a different document.'
		);
		onBack?.();
	}, [toastError, onBack]);

	const configurableFields = React.useMemo(
		() => detectedFields.filter(isConfigurablePdfField),
		[detectedFields]
	);

	const isDetecting = !hasDetectedFields;
	const hasNoFields =
		hasFieldsHint === false ||
		(hasDetectedFields && configurableFields.length === 0);
	const showEditor = !hasNoFields && !isPreviewOpen;

	React.useEffect(() => {
		onOverlayActiveChange?.(!showEditor);
	}, [showEditor, onOverlayActiveChange]);

	const { fieldConfigs, onDataTypeChange, onRenameChange } = useFieldConfigs(
		detectedFields,
		initialFieldConfigs
	);

	const orderedFields = React.useMemo(
		() => sortFieldsByReadingOrder(configurableFields),
		[configurableFields]
	);

	React.useEffect(() => {
		if (selectedFieldId === null && orderedFields.length > 0) {
			setSelectedFieldId(orderedFields[0].id);
		}
	}, [orderedFields, selectedFieldId]);

	const selectedIndex = selectedFieldId
		? orderedFields.findIndex((field) => field.id === selectedFieldId)
		: -1;

	const handleSelectPrev = React.useCallback(() => {
		if (orderedFields.length === 0) return;
		const prevIndex =
			selectedIndex === -1
				? orderedFields.length - 1
				: (selectedIndex - 1 + orderedFields.length) % orderedFields.length;
		setSelectedFieldId(orderedFields[prevIndex].id);
	}, [orderedFields, selectedIndex]);

	const handleSelectNext = React.useCallback(() => {
		if (orderedFields.length === 0) return;
		const nextIndex =
			selectedIndex === -1 ? 0 : (selectedIndex + 1) % orderedFields.length;
		setSelectedFieldId(orderedFields[nextIndex].id);
	}, [orderedFields, selectedIndex]);

	return (
		<>
			<div
				className="configure-fields-modal flex flex-col bg-white overflow-hidden"
				style={{
					width: 1256,
					maxWidth: '100%',
					borderRadius: 28,
					boxShadow: '0px 14px 42px 0px rgba(8, 15, 52, 0.08)',
					visibility: showEditor ? 'visible' : 'hidden',
				}}
			>
				<style>{`
				.configure-fields-modal {
					height: 93vh;
				}
				@supports (height: 100dvh) {
					.configure-fields-modal {
						height: 93dvh;
					}
				}
			`}</style>
				<div
					className="flex items-center justify-between shrink-0"
					style={{ padding: '16px 32px' }}
				>
					<Button
						type="button"
						decoration="ghost"
						onClick={onBack}
						aria-label="Back"
						className="!w-auto !p-0 !border-none opacity-50 hover:opacity-70 transition-opacity"
					>
						<Icon src={Icons.arrowLeft} width={24} height={24} />
					</Button>

					<div className="flex items-center gap-2">
						<Icon src={Icons.documentsEmptyState} width={58} height={36} />
						<Typography
							type="semibold-16"
							className="!text-[19px] text-[#0D0616]"
						>
							Configure Document
						</Typography>
					</div>

					<div className="flex items-center gap-3">
						<TooltipComponent
							side="bottom"
							align="end"
							alignOffset={0}
							className="max-w-[320px]"
							label=""
							customLabel={
								<div className="flex flex-col gap-3 text-left">
									<div>
										<Typography
											type="semibold-12"
											className="!text-[13px] !text-[#0D0616]"
										>
											Text fields:
										</Typography>
										<Typography
											type="regular-12"
											className="!text-[13px] !text-[#0D0616]"
										>
											The default for every field. The client&apos;s answer
											isn&apos;t checked against anything.
										</Typography>
									</div>
									<div>
										<Typography
											type="semibold-12"
											className="!text-[13px] !text-[#0D0616]"
										>
											Validated fields:
										</Typography>
										<Typography
											type="regular-12"
											className="!text-[13px] !text-[#0D0616]"
										>
											Assign a Data Type (Name, Address, Phone, etc.) to compare
											the client&apos;s answer with their personal settings.
											Mismatches are flagged for your review, not blocked from
											being submitted.
										</Typography>
									</div>
									<div>
										<Typography
											type="semibold-12"
											className="!text-[13px] !text-[#0D0616]"
										>
											Signature fields:
										</Typography>
										<Typography
											type="regular-12"
											className="!text-[13px] !text-[#0D0616]"
										>
											Mark fields where the client must provide a signature by
											hand.
										</Typography>
									</div>
								</div>
							}
							trigger={
								<Button
									type="button"
									decoration="none"
									size="fit"
									label="Help"
									icon={Icons.question}
									iconLeft
									className="!w-auto !rounded-full !h-8 !px-3 !border !border-[rgba(13,6,22,0.15)] shrink-0"
									classNameButton="!w-3.5 !h-3.5 !mr-1.5"
									classNameLabel="!font-normal !text-xs !text-[#6E6A73]"
								/>
							}
						/>
						<Button
							type="button"
							decoration="ghost"
							onClick={onClose}
							aria-label="Close"
							className="!w-auto !p-0 !border-none opacity-50 hover:opacity-70 transition-opacity"
						>
							<XMarkIcon className="size-6 text-[#0D0616]" />
						</Button>
					</div>
				</div>

				<div
					className="flex flex-1 min-h-0"
					style={{ borderTop: '1px solid rgba(13, 6, 22, 0.06)' }}
				>
					<div
						className="flex-1 min-w-0 flex flex-col"
						style={{ background: '#F0F0F0' }}
					>
						<DocumentToolbar
							zoomScale={zoomScale}
							onZoomScaleChange={setZoomScale}
							currentPageIndex={currentPageIndex}
							numPages={numPages}
							onPageChange={handlePageChange}
						/>
						<div
							ref={pdfViewportRef}
							className={`flex-1 min-h-0 overflow-auto ${viewportCursorClassName}`}
							style={{ padding: 24 }}
						>
							<div
								className="bg-white shrink-0 h-fit w-fit mx-auto"
								style={{
									padding: 48,
									boxShadow: '0px 2px 16px 0px rgba(0, 0, 0, 0.12)',
									borderRadius: 6,
								}}
							>
								<PdfFieldCanvas
									ref={pdfCanvasRef}
									pdfUrl={pdfUrl}
									onFieldsDetected={handleFieldsDetected}
									onLoadError={handlePdfLoadError}
									fieldConfigs={fieldConfigs}
									selectedFieldId={selectedFieldId}
									onFieldClick={setSelectedFieldId}
									zoomScale={zoomScale}
									onNumPagesChange={setNumPages}
									onCurrentPageChange={setCurrentPageIndex}
								/>
							</div>
						</div>
					</div>

					<div
						className="flex flex-col shrink-0 relative"
						style={{
							width: 332,
							background: '#FFFFFF',
							borderLeft: '1px solid rgba(13, 6, 22, 0.06)',
						}}
					>
						<div
							className="flex items-center justify-between px-4 py-3 shrink-0 gap-2"
							style={{ borderBottom: '1px solid rgba(13, 6, 22, 0.06)' }}
						>
							<div className="flex items-center gap-2 min-w-0">
								<Typography
									type="semibold-12"
									className="!text-[13px] text-[#0D0616] shrink-0"
								>
									Fields Detected
								</Typography>
								<GenericBadge
									label={String(configurableFields.length)}
									colorStatus="gray"
									typographyType="semibold-12"
									classNameContainer="!bg-[#EBDFFA] !rounded-full !py-[2px] !px-[8px] [&_p]:!text-[#8039DF] [&_p]:!text-[11px]"
								/>
							</div>

							<div className="flex items-center gap-1.5 shrink-0">
								<Typography
									type="regular-12"
									className="!text-[11px] text-[#6E6A73] tabular-nums"
								>
									{selectedIndex >= 0 ? selectedIndex + 1 : '–'}/
									{orderedFields.length}
								</Typography>
								<Button
									type="button"
									decoration="ghost"
									onClick={handleSelectPrev}
									disabled={orderedFields.length === 0}
									aria-label="Previous field"
									className="!p-0 !w-5 !h-5 disabled:opacity-30 hover:!bg-[#F4F4F4] transition-colors shrink-0"
								>
									<ChevronLeftIcon className="size-3 text-[#6E6A73]" />
								</Button>
								<Button
									type="button"
									decoration="ghost"
									onClick={handleSelectNext}
									disabled={orderedFields.length === 0}
									aria-label="Next field"
									className="!p-0 !w-5 !h-5 disabled:opacity-30 hover:!bg-[#F4F4F4] transition-colors shrink-0"
								>
									<ChevronRightIcon className="size-3 text-[#6E6A73]" />
								</Button>
							</div>
						</div>

						<FieldsPanel
							detectedFields={orderedFields}
							fieldConfigs={fieldConfigs}
							selectedFieldId={selectedFieldId}
							onSelectField={setSelectedFieldId}
							onDataTypeChange={onDataTypeChange}
							onRenameChange={onRenameChange}
						/>

						<div
							className="shrink-0 flex flex-col gap-2 relative"
							style={{
								padding: 12,
								borderTop: '1px solid rgba(13, 6, 22, 0.06)',
							}}
						>
							<Button
								type="button"
								size="full"
								decoration="line-primary"
								label="Preview"
								icon={Icons.eye}
								iconLeft
								className="!h-[38px]"
								disabled={isDetecting}
								onClick={() => previewModalRef.current?.show()}
							/>
							<Button
								type="button"
								size="full"
								decoration="primary"
								label="Continue"
								className="!h-[38px]"
								disabled={isDetecting}
								onClick={() => setShowConfirmPopover(true)}
							/>

							{showConfirmPopover && (
								<div
									ref={confirmPopoverRef}
									role="dialog"
									aria-label="Confirm fields"
									className="absolute bg-white flex flex-col justify-between"
									style={{
										right: 0,
										bottom: 0,
										width: 384,
										height: 243,
										padding: '32px 40px',
										borderRadius: 24,
										boxShadow: '0px 14px 42px 0px rgba(8, 15, 52, 0.12)',
										zIndex: 10,
									}}
								>
									<div className="flex flex-col gap-2 text-left">
										<Typography
											type="semibold-16"
											className="!text-[16px] text-left text-[#0D0616]"
										>
											Did you confirm that all the fields are correct?
										</Typography>
										<Typography
											type="regular-14"
											className="!text-[14px] !text-[#6E6A73] text-left"
										>
											Ensure that all fields are correctly named and properly
											identified to avoid confusing clients.
										</Typography>
									</div>
									<div className="flex items-center justify-between">
										<Button
											type="button"
											size="fit"
											decoration="secondary"
											label="Back"
											className="!h-[30px]"
											onClick={() => setShowConfirmPopover(false)}
										/>
										<Button
											type="button"
											size="fit"
											decoration="primary"
											label="Confirm"
											className="!h-[30px]"
											onClick={() => {
												setShowConfirmPopover(false);
												onContinue?.(fieldConfigs);
											}}
										/>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				<DesktopMobilePreviewModal
					ref={previewModalRef}
					pdfUrl={pdfUrl}
					file={file}
					fieldConfigs={fieldConfigs}
					onOpenChange={setIsPreviewOpen}
				/>
			</div>

			{hasNoFields && (
				<NoFieldsDetectedModal
					// eslint-disable-next-line @typescript-eslint/no-empty-function
					onCancel={onBack ?? onClose ?? (() => {})}
					onConfirm={() => onContinue?.(fieldConfigs)}
				/>
			)}
		</>
	);
};

export default ConfigureFieldsStep;
